import {
  getMealPhotoFn,
  getWorkoutMealsOptions,
  saveWorkoutMealFn,
  uploadMealPhotoFn,
} from "@/actions/workout-meal/handler";
import type { MealData } from "@/api/db/schema/workout-meals";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useFeatureTour } from "@/lib/tour/useFeatureTour";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Calculator,
  Calendar,
  Camera,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Trash2,
  Utensils,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  WORKOUT_MEAL_TOUR_ID,
  WORKOUT_MEAL_TOUR_VERSION,
  workoutMealTourSteps,
} from "./tour/workoutMealTourSteps";

export function MealPhoto({ photoKey }: { photoKey: string }) {
  const { data, isError, refetch } = useQuery({
    queryKey: ["mealPhoto", photoKey],
    queryFn: () => getMealPhotoFn({ data: { key: photoKey } }),
    staleTime: 240000,
    refetchInterval: 240000,
  });
  if (isError)
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => void refetch()}
      >
        Fotoğrafı tekrar yükle
      </Button>
    );
  return data ? (
    <img
      src={data.url}
      alt="Öğün fotoğrafı"
      className="max-h-48 w-full rounded-lg border border-border bg-background object-contain"
    />
  ) : (
    <p className="text-muted-foreground text-xs">Fotoğraf yükleniyor...</p>
  );
}

const emptyMeal: MealData = {
  calories: 0,
  protein: 0,
  carbs: 0,
  fats: 0,
  photoKey: null,
};

function MealForm({
  programId,
  date,
  phase,
  initialMeal,
  isCoachingClient,
}: {
  programId: string;
  date: string;
  phase: "pre" | "post";
  initialMeal: MealData | null;
  isCoachingClient: boolean;
}) {
  const queryClient = useQueryClient();
  const [meal, setMeal] = useState<MealData>(initialMeal ?? emptyMeal);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto calculated calories: (4 * P) + (4 * C) + (9 * F)
  const calculatedKcal = Math.round(
    Number(meal.protein || 0) * 4 +
      Number(meal.carbs || 0) * 4 +
      Number(meal.fats || 0) * 9,
  );

  const mutation = useMutation({
    mutationFn: async () => {
      let photoKey = meal.photoKey;
      if (file) {
        const upload = await uploadMealPhotoFn({
          data: {
            contentType: file.type as "image/jpeg" | "image/png" | "image/webp",
            size: file.size,
          },
        });
        const response = await fetch(upload.url, {
          method: "PUT",
          headers: { "Content-Type": file.type },
          body: file,
        });
        if (!response.ok) throw new Error("Fotoğraf yüklenemedi.");
        photoKey = upload.key;
      }
      const result = await saveWorkoutMealFn({
        data: { programId, date, phase, meal: { ...meal, photoKey } },
      });
      return { ...result, photoKey };
    },
    onSuccess: (result) => {
      setMeal((previous) => ({ ...previous, photoKey: result.photoKey }));
      setFile(null);
      toast.success(
        result.syncedToCoaching
          ? "Öğün kaydedildi ve koçunuza iletildi."
          : "Öğün kaydedildi.",
      );
      for (const queryKey of [
        ["workoutMeals", date],
        ["getMetricByDate", date],
        ["getTodayMetric"],
        ["getMyMetrics"],
        ["admin", "user-activity"],
      ]) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
    onError: () =>
      toast.error(
        "Öğün kaydedilemedi. Bilgilerinizi koruduk; tekrar deneyebilirsiniz.",
      ),
  });

  const hasData =
    (meal.calories ?? 0) > 0 ||
    (meal.protein ?? 0) > 0 ||
    Boolean(meal.photoKey);

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate();
      }}
    >
      <fieldset disabled={mutation.isPending} className="space-y-3">
        {/* 4 Macro Cards: Compact, Color-coded, High Utility */}
        <div data-tour="workout-meal-macros" className="space-y-2">
          <div className="grid grid-cols-4 gap-2">
            {/* Kalori */}
            <div className="flex flex-col items-center justify-between rounded-xl border border-border/70 bg-card p-2 text-center transition-all focus-within:border-amber-500/80 focus-within:ring-1 focus-within:ring-amber-500/20 sm:p-2.5">
              <span className="font-bold text-[10px] text-amber-500 uppercase tracking-wider dark:text-amber-400">
                Kalori
              </span>
              <Input
                id={`${phase}-calories`}
                type="number"
                min={0}
                max={10000}
                step="1"
                required
                className="h-8 w-full border-none bg-transparent p-0 text-center font-bold text-base tabular-nums shadow-none focus-visible:ring-0 sm:text-lg"
                value={meal.calories || ""}
                placeholder="0"
                onChange={(e) =>
                  setMeal((prev) => ({
                    ...prev,
                    calories: Number(e.target.value) || 0,
                  }))
                }
              />
              <span className="text-[10px] text-muted-foreground">kcal</span>
            </div>

            {/* Protein */}
            <div className="flex flex-col items-center justify-between rounded-xl border border-border/70 bg-card p-2 text-center transition-all focus-within:border-blue-500/80 focus-within:ring-1 focus-within:ring-blue-500/20 sm:p-2.5">
              <span className="font-bold text-[10px] text-blue-500 uppercase tracking-wider dark:text-blue-400">
                Protein
              </span>
              <Input
                id={`${phase}-protein`}
                type="number"
                min={0}
                max={1000}
                step="0.1"
                required
                className="h-8 w-full border-none bg-transparent p-0 text-center font-bold text-base tabular-nums shadow-none focus-visible:ring-0 sm:text-lg"
                value={meal.protein || ""}
                placeholder="0"
                onChange={(e) =>
                  setMeal((prev) => ({
                    ...prev,
                    protein: Number(e.target.value) || 0,
                  }))
                }
              />
              <span className="text-[10px] text-muted-foreground">g</span>
            </div>

            {/* Karb */}
            <div className="flex flex-col items-center justify-between rounded-xl border border-border/70 bg-card p-2 text-center transition-all focus-within:border-orange-500/80 focus-within:ring-1 focus-within:ring-orange-500/20 sm:p-2.5">
              <span className="font-bold text-[10px] text-orange-500 uppercase tracking-wider dark:text-orange-400">
                Karb
              </span>
              <Input
                id={`${phase}-carbs`}
                type="number"
                min={0}
                max={1000}
                step="0.1"
                required
                className="h-8 w-full border-none bg-transparent p-0 text-center font-bold text-base tabular-nums shadow-none focus-visible:ring-0 sm:text-lg"
                value={meal.carbs || ""}
                placeholder="0"
                onChange={(e) =>
                  setMeal((prev) => ({
                    ...prev,
                    carbs: Number(e.target.value) || 0,
                  }))
                }
              />
              <span className="text-[10px] text-muted-foreground">g</span>
            </div>

            {/* Yağ */}
            <div className="flex flex-col items-center justify-between rounded-xl border border-border/70 bg-card p-2 text-center transition-all focus-within:border-emerald-500/80 focus-within:ring-1 focus-within:ring-emerald-500/20 sm:p-2.5">
              <span className="font-bold text-[10px] text-emerald-500 uppercase tracking-wider dark:text-emerald-400">
                Yağ
              </span>
              <Input
                id={`${phase}-fats`}
                type="number"
                min={0}
                max={1000}
                step="0.1"
                required
                className="h-8 w-full border-none bg-transparent p-0 text-center font-bold text-base tabular-nums shadow-none focus-visible:ring-0 sm:text-lg"
                value={meal.fats || ""}
                placeholder="0"
                onChange={(e) =>
                  setMeal((prev) => ({
                    ...prev,
                    fats: Number(e.target.value) || 0,
                  }))
                }
              />
              <span className="text-[10px] text-muted-foreground">g</span>
            </div>
          </div>

          {/* Smart Calorie Quick-fill: 1 tap to calculate */}
          {calculatedKcal > 0 && calculatedKcal !== meal.calories && (
            <div className="flex items-center justify-end px-1">
              <button
                type="button"
                onClick={() =>
                  setMeal((prev) => ({ ...prev, calories: calculatedKcal }))
                }
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 font-medium text-[11px] text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
              >
                <Calculator className="h-3 w-3 text-primary" />
                <span>
                  Makrolardan hesapla:{" "}
                  <strong className="text-primary">
                    {calculatedKcal} kcal
                  </strong>
                </span>
              </button>
            </div>
          )}
        </div>

        {/* Photo Section & Actions */}
        <div data-tour="workout-meal-photo" className="space-y-2">
          {/* Photo Preview if already exists */}
          {meal.photoKey && (
            <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/20 p-2">
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-border">
                <MealPhoto photoKey={meal.photoKey} />
              </div>
              <div className="flex flex-1 items-center justify-between">
                <span className="text-muted-foreground text-xs">
                  Kayıtlı Fotoğraf
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-destructive text-xs hover:bg-destructive/10"
                  onClick={() => {
                    setMeal((prev) => ({ ...prev, photoKey: null }));
                    setFile(null);
                  }}
                >
                  <Trash2 className="mr-1 h-3 w-3" /> Sil
                </Button>
              </div>
            </div>
          )}

          {/* Bottom Actions Bar */}
          <div className="flex items-center justify-between gap-2 pt-0.5">
            {/* Photo upload trigger */}
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(event) => {
                  const selected = event.target.files?.[0];
                  if (!selected) return;
                  if (
                    !["image/jpeg", "image/png", "image/webp"].includes(
                      selected.type,
                    ) ||
                    selected.size > 10 * 1024 * 1024
                  ) {
                    toast.error("En fazla 10 MB JPEG, PNG veya WebP seçin.");
                    event.target.value = "";
                    return;
                  }
                  setFile(selected);
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 rounded-lg border-border/70 px-2.5 text-xs hover:bg-muted/40"
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera className="h-3.5 w-3.5 text-muted-foreground" />
                <span>
                  {file
                    ? "Fotoğrafı Değiştir"
                    : meal.photoKey
                      ? "Yeni Fotoğraf"
                      : "Fotoğraf Ekle"}
                </span>
              </Button>
              {file && (
                <span className="max-w-[110px] truncate text-[11px] text-primary sm:max-w-[180px]">
                  {file.name}
                </span>
              )}
            </div>

            {/* Submit & Coaching note */}
            <div className="flex items-center gap-2">
              {isCoachingClient && (
                <span className="hidden text-[11px] text-muted-foreground sm:inline">
                  Koçunuz görebilir
                </span>
              )}
              {/* Submit button */}
              <Button
                type="submit"
                size="sm"
                className="h-8 rounded-lg bg-primary px-3 font-semibold text-primary-foreground text-xs shadow-xs hover:bg-primary/90"
                disabled={mutation.isPending}
              >
                {mutation.isPending
                  ? "Kaydediliyor..."
                  : hasData
                    ? "Güncelle"
                    : "Kaydet"}
              </Button>
            </div>
          </div>
        </div>
      </fieldset>
    </form>
  );
}

/**
 * Single WorkoutMealCard for backwards compatibility
 */
export function WorkoutMealCard({
  programId,
  date,
  phase,
  isCoachingClient,
}: {
  programId: string;
  date: string;
  phase: "pre" | "post";
  isCoachingClient: boolean;
}) {
  const { data, isPending, isError, refetch } = useQuery(
    getWorkoutMealsOptions(date),
  );
  const existing = data?.find((entry) => entry.phase === phase)?.data ?? null;

  return (
    <Card className="border border-border/80 bg-card shadow-sm">
      <CardContent className="p-4 sm:p-5">
        <h3 className="mb-3 font-semibold text-sm">
          {phase === "pre" ? "Antrenman Öncesi Öğün" : "Antrenman Sonrası Öğün"}
        </h3>
        {isPending ? (
          <p className="text-muted-foreground text-xs">Yükleniyor...</p>
        ) : isError ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
          >
            Tekrar Yükle
          </Button>
        ) : (
          <MealForm
            key={`${programId}-${date}-${phase}`}
            programId={programId}
            date={date}
            phase={phase}
            initialMeal={existing}
            isCoachingClient={isCoachingClient}
          />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Unified, sleek, collapsible meal tracking section for workout page
 */
export function WorkoutMealSection({
  programId,
  date,
  onDateChange,
  isCoachingClient,
}: {
  programId: string;
  date: string;
  onDateChange: (date: string) => void;
  isCoachingClient: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"pre" | "post">("pre");

  const tour = useFeatureTour(WORKOUT_MEAL_TOUR_ID, workoutMealTourSteps, {
    version: WORKOUT_MEAL_TOUR_VERSION,
    autoStart: isCoachingClient,
    delayMs: 800,
  });

  // Automatically expand the meal card if the tour is opened
  useEffect(() => {
    if (tour.isOpen) {
      setIsOpen(true);
    }
  }, [tour.isOpen]);

  const { data, isPending } = useQuery({
    ...getWorkoutMealsOptions(date),
    enabled: isCoachingClient,
  });
  const preMeal = data?.find((entry) => entry.phase === "pre")?.data ?? null;
  const postMeal = data?.find((entry) => entry.phase === "post")?.data ?? null;

  const preCal = preMeal?.calories ?? 0;
  const postCal = postMeal?.calories ?? 0;
  const totalCal = preCal + postCal;
  const hasAnyMeal = totalCal > 0;

  if (!isCoachingClient) {
    return null;
  }

  return (
    <div
      data-tour="workout-meal-card"
      className="overflow-hidden rounded-2xl border border-border/70 bg-card/60 shadow-xs backdrop-blur-xs transition-all"
    >
      {/* Header bar - sleek, single-line on mobile, never wraps awkwardly */}
      <div
        data-tour="workout-meal-header"
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen((prev) => !prev);
          }
        }}
        className="flex cursor-pointer select-none items-center justify-between p-3.5 transition-colors hover:bg-muted/20 sm:p-4"
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Utensils className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate font-semibold text-foreground text-sm">
                Antrenman Beslenmesi
              </span>
              {hasAnyMeal ? (
                <Badge
                  variant="secondary"
                  className="h-5 bg-primary/15 px-1.5 font-bold text-[11px] text-primary"
                >
                  {totalCal} kcal
                </Badge>
              ) : (
                <span className="hidden text-[11px] text-muted-foreground sm:inline">
                  (İsteğe bağlı)
                </span>
              )}
            </div>
            <p className="truncate text-[11px] text-muted-foreground">
              {hasAnyMeal
                ? `${preCal > 0 ? `Öncesi: ${preCal} kcal` : ""}${preCal > 0 && postCal > 0 ? " · " : ""}${postCal > 0 ? `Sonrası: ${postCal} kcal` : ""}`
                : "Öncesi ve sonrası makrolarını kaydet"}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {/* Replay Tour Trigger */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              tour.start();
            }}
            title="Özellik turunu başlat"
            aria-label="Özellik turunu başlat"
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
          >
            <HelpCircle className="h-3.5 w-3.5" />
          </button>

          {!isOpen && (
            <span className="hidden font-medium text-primary text-xs sm:inline">
              {hasAnyMeal ? "Düzenle" : "+ Öğün Ekle"}
            </span>
          )}
          <div className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/50">
            {isOpen ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded panel */}
      {isOpen && (
        <div className="space-y-3 border-border/50 border-t bg-muted/5 p-3.5 sm:p-4">
          {/* Single-row Phase Toggle + Date selector */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            {/* Phase Tabs */}
            <div
              data-tour="workout-meal-phases"
              className="inline-flex rounded-lg bg-muted/60 p-0.5 font-medium text-xs"
            >
              <button
                type="button"
                onClick={() => setActiveTab("pre")}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs transition-all",
                  activeTab === "pre"
                    ? "bg-background font-semibold text-foreground shadow-xs"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <span>Antrenman Öncesi</span>
                {preCal > 0 && (
                  <span className="font-bold text-[10px] text-primary">
                    ({preCal})
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("post")}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs transition-all",
                  activeTab === "post"
                    ? "bg-background font-semibold text-foreground shadow-xs"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <span>Antrenman Sonrası</span>
                {postCal > 0 && (
                  <span className="font-bold text-[10px] text-primary">
                    ({postCal})
                  </span>
                )}
              </button>
            </div>

            {/* Compact date selector */}
            <div className="flex items-center gap-1.5 rounded-md border border-border/50 bg-background px-2 py-1 text-muted-foreground text-xs shadow-2xs">
              <Calendar className="h-3 w-3 shrink-0 text-muted-foreground" />
              <input
                type="date"
                value={date}
                onChange={(e) => {
                  if (/^\d{4}-\d{2}-\d{2}$/.test(e.target.value)) {
                    onDateChange(e.target.value);
                  }
                }}
                className="w-[100px] cursor-pointer border-0 bg-transparent text-foreground text-xs focus:outline-none"
              />
            </div>
          </div>

          {/* Active Phase Form */}
          {isPending ? (
            <p className="py-2 text-center text-muted-foreground text-xs">
              Yükleniyor...
            </p>
          ) : (
            <MealForm
              key={`section-${programId}-${date}-${activeTab}`}
              programId={programId}
              date={date}
              phase={activeTab}
              initialMeal={activeTab === "pre" ? preMeal : postMeal}
              isCoachingClient={isCoachingClient}
            />
          )}
        </div>
      )}
    </div>
  );
}
